import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EXPORT_FORMATS, SPECTRAL_TYPE } from "../../utils/constants";
import CheckBox from "../CheckBox/CheckBox";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";
import BaseModal from "./BaseModal";
import SimpleNotification from "../Notifications/SimpleNotification";

const ExportScansModal = ({
    isVisible,
    setIsVisible,
    submit,
    isResultsHidden,
    isSpectralDataHidden,
    isMetadataHidden,
    isSubmitting,
    rowsCount,
    isReflectance,
}) => {
    const { t } = useTranslation();
    const [columns, setColumns] = useState({
        sampleName: true,
        results: false,
    });
    const [metadataStatus, setMetadataStatus] = useState(
        isMetadataHidden ? "unChecked" : "checked"
    );
    const [metadata, setMetadata] = useState({
        capturedAt: false,
        deviceId: false,
        createdBy: false,
        onBehalfOf: false,
        lotName: false,
        note: false,
        location: false,
    });
    const [spectralData, setSpectralData] = useState({
        type: SPECTRAL_TYPE.readings,
        isIncluded: false,
    });

    const [isFormatDisabled, setIsFormatDisabled] = useState(false);
    const MAX_SCANS_FOR_PDF = 5;

    const EXPORT_SCANS_FORMATS = Object.freeze({
        csv: {
            label: t(EXPORT_FORMATS.csv),
            value: EXPORT_FORMATS.csv,
        },
        pdf: {
            label: (
                <div className="flex items-center justify-center">
                    <div>PDF (Certificate)</div>
                    {rowsCount > MAX_SCANS_FOR_PDF && (
                        <div className="text-primary-color-orange ml-1 text-xxs">
                            {t("maxReach", {
                                number: MAX_SCANS_FOR_PDF,
                                entity: t("scans"),
                            })}
                        </div>
                    )}
                </div>
            ),
            value: EXPORT_FORMATS.pdf,
        },
    });

    const [formatType, setFormatType] = useState(EXPORT_SCANS_FORMATS.csv);

    const spectralDataOptions = [
        {
            name: SPECTRAL_TYPE.readings,
            label: t("readings"),
            value: SPECTRAL_TYPE.readings,
        },
        {
            name: SPECTRAL_TYPE.average,
            label: t("average"),
            value: SPECTRAL_TYPE.average,
        },
    ];

    const format_options = [...Object.values(EXPORT_SCANS_FORMATS)];

    useEffect(() => {
        if (spectralData.isIncluded || rowsCount > MAX_SCANS_FOR_PDF) {
            disablePdfFormat();
        } else {
            setIsFormatDisabled(false);
        }
    }, [spectralData, rowsCount]);

    useEffect(() => {
        setColumns((prevState) => {
            return {
                ...prevState,
                results: !isResultsHidden,
            };
        });
        setMetadataStatus(
            isMetadataHidden
                ? "unChecked"
                : isMetadataIntermediate()
                ? "intermediate"
                : "checked"
        );
        setMetadataObject(!isMetadataHidden);
        setSpectralData((prevState) => {
            return {
                ...prevState,
                isIncluded: !isSpectralDataHidden,
            };
        });
    }, [isResultsHidden, isSpectralDataHidden, isMetadataHidden]);

    useEffect(() => {
        if (!isMetadataValid() && !isMetadataInValid()) {
            setMetadataStatus("intermediate");
        } else if (isMetadataValid()) {
            setMetadataStatus("checked");
        } else {
            setMetadataStatus("unChecked");
        }
    }, [metadata]);

    const disablePdfFormat = () => {
        setFormatType(EXPORT_SCANS_FORMATS.csv);
        setIsFormatDisabled(true);
    };

    const handleColumnsCheckBox = (e) => {
        setColumns((prevState) => ({
            ...prevState,
            [e.target.name]: !prevState[e.target.name],
        }));
    };

    const handleMetadataCheckBox = (e) => {
        setMetadata((prevState) => ({
            ...prevState,
            [e.target.name]: !prevState[e.target.name],
        }));
    };

    const setMetadataObject = (value) => {
        setMetadata((prevState) => {
            return {
                ...prevState,
                capturedAt: value,
                deviceId: value,
                createdBy: value,
                onBehalfOf: value,
                lotName: value,
                note: value,
                location: value,
            };
        });
    };

    const handleMainMetadataCheckBox = () => {
        if (metadataStatus === "intermediate") {
            setMetadataStatus("checked");
            setMetadataObject(true);
        } else if (metadataStatus === "checked") {
            setMetadataStatus("unChecked");
            setMetadataObject(false);
        } else {
            setMetadataStatus("checked");
            setMetadataObject(true);
        }
    };

    const handleSpectralDataCheckBox = () => {
        setSpectralData((prevState) => {
            return {
                ...prevState,
                isIncluded: !prevState["isIncluded"],
            };
        });
    };

    const isMetadataIntermediate = () => {
        return !isMetadataValid() && !isMetadataInValid;
    };
    const isMetadataValid = () =>
        Object.values(metadata).every((p) => p === true);
    const isMetadataInValid = () =>
        Object.values(metadata).every((p) => p === false);

    const handleBooleanRadioChange = (e) => {
        setSpectralData((prevState) => {
            return {
                ...prevState,
                type: e.target.value,
            };
        });
    };

    const columnOptions = [
        {
            name: "sampleName",
            label: t("sampleName"),
            onChange: handleColumnsCheckBox,
            value: columns["sampleName"],
            disabled: true,
            className: "gap-4 mb-4",
        },
        {
            name: "results",
            label: t("results"),
            onChange: handleColumnsCheckBox,
            value: columns["results"],
            disabled: formatType.value === EXPORT_FORMATS.pdf,
            className: "gap-4 mb-4",
        },
        {
            name: "metadata",
            label: t("metadata"),
            onChange: handleMainMetadataCheckBox,
            value:
                metadataStatus === "checked"
                    ? true
                    : metadataStatus === "unChecked"
                    ? false
                    : null,
            isIntermediate: metadataStatus === "intermediate",
            className: "gap-2 mb-1",
            subColumns: [
                {
                    name: "capturedAt",
                    label: t("capturedAt"),
                    onChange: handleMetadataCheckBox,
                    value: metadata["capturedAt"],
                    disabled: metadataStatus === "unChecked",
                },
                {
                    name: "deviceId",
                    label: t("deviceId"),
                    onChange: handleMetadataCheckBox,
                    value: metadata["deviceId"],
                    disabled: metadataStatus === "unChecked",
                },
                {
                    name: "createdBy",
                    label: t("createdBy"),
                    onChange: handleMetadataCheckBox,
                    value: metadata["createdBy"],
                    disabled: metadataStatus === "unChecked",
                },
                {
                    name: "onBehalfOf",
                    label: t("onBehalfOf"),
                    onChange: handleMetadataCheckBox,
                    value: metadata["onBehalfOf"],
                    disabled: metadataStatus === "unChecked",
                },
                {
                    name: "lotName",
                    label: t("lotName"),
                    onChange: handleMetadataCheckBox,
                    value: metadata["lotName"],
                    disabled: metadataStatus === "unChecked",
                },
                {
                    name: "note",
                    label: t("notes"),
                    onChange: handleMetadataCheckBox,
                    value: metadata["note"],
                    disabled: metadataStatus === "unChecked",
                },
                {
                    name: "location",
                    label: t("location"),
                    onChange: handleMetadataCheckBox,
                    value: metadata["location"],
                    disabled: metadataStatus === "unChecked",
                },
            ],
        },
        {
            name: "spectralData",
            label: t("spectralData"),
            onChange: handleSpectralDataCheckBox,
            value: spectralData.isIncluded,
            className: "gap-2 mb-1",
            radio: {
                name: "spectralData",
                options: spectralDataOptions,
                onChange: handleBooleanRadioChange,
                disabled: !spectralData.isIncluded,
                value: spectralData.type,
            },
        },
    ];

    const handleFormatChange = (e) => {
        if (e.target.value === EXPORT_FORMATS.pdf)
            setColumns((prevState) => ({
                ...prevState,
                results: true,
            }));
        setFormatType({ value: e.target.value });
    };

    const renderCheckbox = (option, labelClassName) => {
        return (
            <div
                key={option.name}
                className={`grid ${option.className} grid-cols-1`}
            >
                <CheckBox
                    key={option.name}
                    name={option.name}
                    onChangeHandler={option.onChange}
                    label={option.label}
                    checked={option.value}
                    isIntermediate={option.isIntermediate}
                    disabled={option.disabled}
                    labelClassName={labelClassName}
                />
            </div>
        );
    };

    return (
        <BaseModal
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            isSubmitting={isSubmitting}
            title={t("exportScansToCsv")}
            titleIcon="fas fa-file-download"
            action={{
                submitAction: () =>
                    submit(
                        {
                            sampleName: columns.sampleName,
                            ...metadata,
                            spectralData,
                            results: columns.results,
                        },
                        formatType.value
                    ),
                disabled:
                    Object.values(columns).filter((column) => column).length ===
                        0 &&
                    Object.values(metadata).filter((column) => column)
                        .length === 0 &&
                    !spectralData.isIncluded,
            }}
            isSmall
        >
            {!isReflectance && (
                <SimpleNotification message={t("absorbanceExportMessage")} />
            )}
            <div className="mb-4">
                <div className="input-group mb-0">
                    <label className="uppercase mb-0">{t("format")}</label>
                </div>
                <hr className="mb-3" />
                <VerticalRadioButtons
                    name={"format"}
                    options={format_options}
                    onChangeHandler={handleFormatChange}
                    disabled={isFormatDisabled}
                    checkedReference={formatType.value}
                    wrapperClassName="mx-3"
                />
            </div>
            <div className="flex items-end justify-between mb-1">
                <div className="input-group mb-0">
                    <label className="uppercase mb-0">
                        {t("selectColumns")}
                    </label>
                </div>
                <label className="text-tiny opacity-70">
                    {`${t("rows")}: `}
                    <span className="text-primary-color-orange">
                        {rowsCount}
                    </span>
                </label>
            </div>
            <hr className="mb-3" />
            {columnOptions.map((option) => {
                return (
                    <div key={option.name}>
                        {renderCheckbox(
                            option,
                            "text-primary-color-dark-blue text-sm font-medium"
                        )}
                        {option.radio ? (
                            <>
                                <hr className="mb-3" />
                                <VerticalRadioButtons
                                    name={option.radio.name}
                                    options={option.radio.options}
                                    onChangeHandler={option.radio.onChange}
                                    disabled={option.radio.disabled}
                                    checkedReference={option.radio.value}
                                    wrapperClassName="mx-3 my-1"
                                />
                            </>
                        ) : option.subColumns ? (
                            <>
                                <hr className="mb-3" />
                                <div className="grid grid-cols-3 gap-2 mb-4 mx-6">
                                    {option.subColumns.map((subOption) => {
                                        return renderCheckbox(
                                            subOption,
                                            "text-primary-blue"
                                        );
                                    })}
                                </div>
                            </>
                        ) : null}
                    </div>
                );
            })}
        </BaseModal>
    );
};

export default ExportScansModal;
